import { useCallback, useRef } from "react";
import type { Ear } from "../type/tonalTest";
import { dbToVolume } from "./audioLevels";

const createNoiseBuffer = (ctx: AudioContext) => {
  const bufferSize = 2 * ctx.sampleRate;
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i += 1) {
    data[i] = Math.random() * 2 - 1;
  }
  return buffer;
};

// Continuous masking noise played into one ear, used by the "SZUM" toggle.
export function useNoiseGenerator() {
  const audioCtxRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<AudioBufferSourceNode | null>(null);
  const gainRef = useRef<GainNode | null>(null);
  const pannerRef = useRef<StereoPannerNode | null>(null);

  const getContext = useCallback(() => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new AudioContext();
    }
    return audioCtxRef.current;
  }, []);

  const update = useCallback((ear: Ear, db: number) => {
    if (pannerRef.current) pannerRef.current.pan.value = ear === "left" ? -1 : 1;
    if (gainRef.current) gainRef.current.gain.value = dbToVolume(db);
  }, []);

  const start = useCallback((ear: Ear, db: number) => {
    if (sourceRef.current) {
      update(ear, db);
      return;
    }

    const ctx = getContext();
    const source = ctx.createBufferSource();
    source.buffer = createNoiseBuffer(ctx);
    source.loop = true;

    const gain = ctx.createGain();
    gain.gain.value = dbToVolume(db);

    const panner = new StereoPannerNode(ctx, { pan: ear === "left" ? -1 : 1 });

    source.connect(gain).connect(panner).connect(ctx.destination);
    source.start();

    sourceRef.current = source;
    gainRef.current = gain;
    pannerRef.current = panner;
  }, [getContext, update]);

  const stop = useCallback(() => {
    if (sourceRef.current) {
      try { sourceRef.current.stop(); } catch { /* already stopped */ }
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }
    if (gainRef.current) {
      gainRef.current.disconnect();
      gainRef.current = null;
    }
    if (pannerRef.current) {
      pannerRef.current.disconnect();
      pannerRef.current = null;
    }
  }, []);

  return { start, update, stop };
}
